import { z } from 'zod';
import { defineProxyApi, JsonValueSchema, ProxyRouteQuerySchema, } from '../base.js';
/*
  These environment variables are used to create the Axios client
  used by the chat-api to proxy requests.
*/
export const ManagementAppProxyConfigSchema = z.object({
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_API_URL: z
        .url()
        .optional()
        .describe('Base URL for the management app custom-page proxy target.'),
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_AUTH_MODE: z
        .enum(['oauth-client-credentials', 'internal-delegation-jwt'])
        .prefault('oauth-client-credentials')
        .describe('Auth mode used for management app custom-page proxy requests.'),
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_API_PATH: z
        .string()
        .prefault('/api/v1')
        .describe('Base API path for the management app custom-page proxy target.'),
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_CLIENT_ID: z
        .string()
        .optional()
        .describe('OAuth client ID used for management app custom-page proxy token retrieval.'),
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_CLIENT_SECRET: z
        .string()
        .optional()
        .describe('OAuth client secret used for management app custom-page proxy token retrieval.'),
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_TOKEN_URL: z
        .url()
        .optional()
        .describe('OAuth token endpoint used for management app custom-page proxy token retrieval.'),
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_SCOPES: z
        .string()
        .prefault('access_as_user')
        .describe('OAuth scopes used for management app custom-page proxy token retrieval.'),
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_SCOPE: z
        .string()
        .optional()
        .describe('Delegation JWT scope used for management app custom-page proxy token generation.'),
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_AUDIENCE: z
        .string()
        .optional()
        .describe('OAuth audience used for management app custom-page proxy token retrieval.'),
    CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_ISSUER: z
        .string()
        .optional()
        .describe('OAuth issuer used for management app custom-page proxy token retrieval.'),
});
/*
  Define all proxy routes here. The chat-api will use these defintiions to validate requests
  and to construct the proxied API client used by components in `custom-pages-registry`.
*/
export const managementAppProxyApi = defineProxyApi({
    clientKey: 'management-app',
    routes: {
        'get-page-templates': {
            method: 'GET',
            path: '/pages/templates',
            timeoutMs: 15000,
            requestSchema: z.null(),
            responseSchema: JsonValueSchema,
        },
    },
    configSchema: ManagementAppProxyConfigSchema,
    buildHttpClientConfig: (config) => {
        if (!config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_API_URL) {
            throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_API_URL');
        }
        if (config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_AUTH_MODE === 'oauth-client-credentials') {
            if (!config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_CLIENT_ID) {
                throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_CLIENT_ID');
            }
            if (!config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_CLIENT_SECRET) {
                throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_CLIENT_SECRET');
            }
            if (!config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_TOKEN_URL) {
                throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_TOKEN_URL');
            }
            if (!config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_ISSUER) {
                throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_ISSUER');
            }
            if (!config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_AUDIENCE) {
                throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_AUDIENCE');
            }
            return {
                apiUrl: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_API_URL,
                apiPath: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_API_PATH,
                auth: {
                    type: 'oauth-client-credentials',
                    clientId: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_CLIENT_ID,
                    clientSecret: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_CLIENT_SECRET,
                    tokenUrl: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_TOKEN_URL,
                    scope: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_SCOPES,
                    issuer: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_ISSUER,
                    audience: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_AUDIENCE,
                },
            };
        }
        if (!config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_AUDIENCE) {
            throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_AUDIENCE');
        }
        if (!config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_SCOPE) {
            throw new Error('Missing required proxy config: CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_SCOPE');
        }
        return {
            apiUrl: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_API_URL,
            apiPath: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_API_PATH,
            auth: {
                type: 'internal-delegation-jwt',
                audience: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_AUDIENCE,
                scope: config.CUSTOM_PAGE_ROUTE_MANAGEMENT_APP_SCOPE,
            },
        };
    },
});
export const MANAGEMENT_APP_PROXY_CLIENT_KEY = managementAppProxyApi.clientKey;
export const ManagementAppRouteLabelSchema = managementAppProxyApi.routeLabelSchema;
export const ManagementAppProxyQuerySchema = ProxyRouteQuerySchema;
export const ManagementAppProxyRequestSchema = managementAppProxyApi.proxyRequestSchema;
export function getManagementAppRouteDefinition(routeLabel) {
    return managementAppProxyApi.getRouteDefinition(routeLabel);
}
